import { getSiteOrigin, resolveAbsoluteUrl } from './siteConfig';
import { CALCULATORS } from '../hooks/useCalculators';

export function buildArticleJsonLd(post) {
  if (!post?.slug) return null;

  const siteUrl = getSiteOrigin();
  const url = `${siteUrl}/post/${post.slug}`;
  const image = resolveAbsoluteUrl(post.featured_image);
  const description = post.excerpt || post.caption || '';
  const dateModified =
    post.last_editorial_update || post.updatedAt || post.published_at;

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: post.title,
    description,
    url,
    datePublished: post.published_at || undefined,
    dateModified: dateModified || undefined,
    author: {
      '@type': 'Organization',
      name: 'PaisaWise',
      url: siteUrl,
    },
    publisher: {
      '@type': 'Organization',
      name: 'PaisaWise',
      url: siteUrl,
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': url,
    },
  };

  if (image) {
    jsonLd.image = [image];
  }

  if (post.reading_time) {
    jsonLd.timeRequired = `PT${post.reading_time}M`;
  }

  return jsonLd;
}

export function buildBreadcrumbListJsonLd(items) {
  const list = (items || []).filter(Boolean);
  if (!list.length) return null;
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: list.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url,
    })),
  };
}

export function buildPostBreadcrumbJsonLd(post) {
  const siteUrl = getSiteOrigin();
  const items = [{ name: 'Home', url: siteUrl }];
  if (post?.category?.slug) {
    items.push({
      name: post.category.name || post.category.slug,
      url: `${siteUrl}/category/${post.category.slug}`,
    });
  }
  items.push({
    name: post.title,
    url: `${siteUrl}/post/${post.slug}`,
  });
  return buildBreadcrumbListJsonLd(items);
}

export function buildCollectionPageJsonLd({ name, slug, description, type = 'category' }) {
  if (!slug) return null;
  const siteUrl = getSiteOrigin();
  const path = type === 'tag' ? `/tag/${slug}` : `/category/${slug}`;
  return {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name,
    description: description || `${name} on PaisaWise`,
    url: `${siteUrl}${path}`,
    isPartOf: {
      '@type': 'WebSite',
      name: 'PaisaWise',
      url: siteUrl,
    },
  };
}

export function buildWebApplicationJsonLd({ title, description, path }) {
  if (!path) return null;
  const siteUrl = getSiteOrigin();
  return {
    '@context': 'https://schema.org',
    '@type': 'WebApplication',
    name: title,
    description,
    url: `${siteUrl}${path}`,
    applicationCategory: 'FinanceApplication',
    operatingSystem: 'Any',
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'INR',
    },
    provider: {
      '@type': 'Organization',
      name: 'PaisaWise',
      url: siteUrl,
    },
  };
}

export function buildPostJsonLdBundle(post) {
  const article = buildArticleJsonLd(post);
  const breadcrumb = buildPostBreadcrumbJsonLd(post);
  const geo = buildPostGeoJsonLd(post);
  return [article, breadcrumb, ...geo].filter(Boolean);
}

function extractTakeawayItemsFromHtml(html) {
  if (!/\bpw-keytakeaways\b/i.test(String(html || ''))) return [];
  const blockMatch = String(html).match(/<div[^>]*class="[^"]*\bpw-keytakeaways\b[^"]*"[^>]*>([\s\S]*?)<\/div>/i);
  const inner = blockMatch ? blockMatch[1] : html;
  const items = [];
  const re = /<li[^>]*>([\s\S]*?)<\/li>/gi;
  let m;
  while ((m = re.exec(inner))) {
    const text = m[1].replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
    if (text) items.push(text);
  }
  return items;
}

export function buildPostGeoJsonLd(post) {
  if (!post?.slug) return [];
  const siteUrl = getSiteOrigin();
  const url = `${siteUrl}/post/${post.slug}`;
  const out = [];

  const takeaways = extractTakeawayItemsFromHtml(post.content);
  const summary = takeaways.join(' ');
  if (summary) {
    out.push({
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: post.title,
      url,
      description: summary.slice(0, 500),
      speakable: {
        '@type': 'SpeakableSpecification',
        cssSelector: ['#seo-geo-crawl-data .seo-geo-in-short'],
      },
    });
  }

  const faqs = Array.isArray(post.geo_faq)
    ? post.geo_faq.filter((r) => r?.question?.trim() && r?.answer?.trim())
    : [];
  if (faqs.length) {
    out.push({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      url,
      mainEntity: faqs.map(({ question, answer }) => ({
        '@type': 'Question',
        name: question.trim(),
        acceptedAnswer: { '@type': 'Answer', text: answer.trim() },
      })),
    });
  }

  return out;
}

export function buildCalculatorJsonLd(pathname) {
  const calc = CALCULATORS.find((c) => c.route_path === pathname);
  if (!calc) return null;
  return buildWebApplicationJsonLd({
    title: calc.title,
    description: calc.description,
    path: calc.route_path,
  });
}
